<template>
    <div class="graph-toolbar">
        <div class="toolbar-section">
            <h3>API 操作示例</h3>
            <div class="form-group">
                <label>节点名称</label>
                <div class="autocomplete-wrapper">
                    <input v-model="apiNodeName" type="text" placeholder="输入节点名称" class="form-control"
                        @input="handleNodeNameInput" @focus="showSuggestions = true" @keydown="handleKeydown" />
                    <div v-if="showSuggestions && (exactMatches.length > 0 || fuzzyMatches.length > 0)"
                        class="suggestions-dropdown">
                        <!-- 精确匹配结果（蓝色显示） -->
                        <div v-for="node in exactMatches" :key="node.id" class="suggestion-item exact-match"
                            @click="selectSuggestion(node.name)">
                            {{ node.name }}
                        </div>
                        <!-- 模糊匹配结果（黄色显示） -->
                        <div v-for="node in fuzzyMatches" :key="node.id" class="suggestion-item fuzzy-match"
                            @click="selectSuggestion(node.name)">
                            {{ node.name }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <label>节点描述</label>
                <input v-model="apiNodeDesc" type="text" placeholder="输入节点描述" class="form-control"
                    @input="handleNodeDescInput" />
            </div>
            <div class="form-group">
                <label>图片链接（待实现）</label>
                <input v-model="apiNodeImage" type="text" placeholder="输入图片URL" class="form-control" />
            </div>
            <button @click="addNodeAPI" class="btn btn-secondary btn-block">API添加节点</button>
            <!-- 只有当选中节点时才显示"添加为子节点"按钮 -->
            <!-- <button v-if="selectedNode" @click="addNodeToChild" class="btn btn-secondary btn-block">添加为子节点</button> -->
            <!-- 下拉选项 -->
            <div v-if="selectedNode" class="form-group" style="margin-top: 15px;">
                <label>关系类型</label>
                <select v-model="selectedGraphTypeId" class="form-control">
                    <option v-for="type in graphTypes" :key="type.id" :value="type.id">
                        {{ type.name }}
                    </option>
                </select>
                <div style="margin-top: 15px;"></div>
                <button @click="addNodeToChild" class="btn btn-secondary btn-block">添加为fromNode节点(子节点)</button>
                <!-- 添加一个间隔 -->
                <div style="margin-top: 15px;"></div>
                <button @click="addChildNode" class="btn btn-secondary btn-block">加为toNode节点(父节点)</button>
            </div>

            <div class="form-group" style="margin-top: 15px;">
                <label>源节点</label>
                <div class="node-input-container">
                    <input v-model="sourceNodeDisplay" type="text" placeholder="点击节点选择源节点" class="form-control"
                        readonly />
                    <button @click="swapNodes" class="swap-btn" title="交换源节点和目标节点">
                        ⇄
                    </button>
                </div>
            </div>
            <div class="form-group">
                <label>目标节点</label>
                <input v-model="targetNodeDisplay" type="text" placeholder="点击节点选择目标节点" class="form-control" readonly />
            </div>

            <button @click="addRelationship" class="btn btn-secondary btn-block">节点绑定关系</button>


        </div>


    </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useKnowledgeGraphStore } from '../stores/knowledgeGraphStore.js'

export default {
    name: 'GraphToolbar',
    props: {
        selectedNode: {
            type: Object,
            default: null
        }
    },
    emits: ['add-node', 'add-edge', 'add-group', 'filter-change', 'search'],
    expose: ['selectedGraphTypeId'],
    setup(props, { emit }) {
        const store = useKnowledgeGraphStore()

        // API操作数据
        const apiNodeName = ref('')
        const apiNodeDesc = ref('')
        const apiNodeImage = ref('')
        const searchNodeName = ref('')
        const sourceNodeId = ref('')
        const targetNodeId = ref('')
        const relationshipDesc = ref('')
        const groupName = ref('')
        const groupColor = ref('#5B8FF9')
        const selectedGraphTypeId = ref('')

        // 自动补全相关数据
        const showSuggestions = ref(false)
        const exactMatches = ref([])
        const fuzzyMatches = ref([])
        const searchTimeout = ref(null)

        // 节点显示文本
        const sourceNodeDisplay = ref('')
        const targetNodeDisplay = ref('')

        // 筛选分组
        const filterGroups = ref([])

        // 搜索文本
        const searchText = ref('')

        // 计算属性
        const nodes = computed(() => store.nodes)
        const groups = computed(() => store.groups)
        const graphTypes = computed(() => store.graphTypes)

        // 监听selectedNode变化
        watch(() => props.selectedNode, (newSelectedNode, oldSelectedNode) => {
            if (newSelectedNode) {
                // 如果之前没有选中节点，则设置为源节点
                if (!oldSelectedNode) {
                    sourceNodeId.value = newSelectedNode.id
                    sourceNodeDisplay.value = `${newSelectedNode.id} - ${newSelectedNode.name}`
                } else {
                    // 如果之前已有选中节点，则将之前的源节点设为目标节点，新节点设为源节点
                    targetNodeId.value = sourceNodeId.value
                    targetNodeDisplay.value = sourceNodeDisplay.value

                    sourceNodeId.value = newSelectedNode.id
                    sourceNodeDisplay.value = `${newSelectedNode.id} - ${newSelectedNode.name}`
                }
            } else {
                // 如果取消选择节点，清空源节点
                sourceNodeId.value = ''
                sourceNodeDisplay.value = ''
            }
        })

        // 处理节点名称输入
        const handleNodeNameInput = async () => {
            console.log('handleNodeNameInput')
            // 触发筛选事件，即使输入为空也要触发以清除高亮
            emit('filter-change', {
                searchText: apiNodeName.value
            })

            // 如果输入为空，隐藏建议列表
            if (!apiNodeName.value.trim()) {
                showSuggestions.value = false
                exactMatches.value = []
                fuzzyMatches.value = []
                return
            }

            // 显示建议列表
            showSuggestions.value = true

            // 清除之前的定时器
            if (searchTimeout.value) {
                clearTimeout(searchTimeout.value)
            }

            // 设置新的定时器，延迟执行搜索以避免频繁请求
            searchTimeout.value = setTimeout(async () => {
                try {
                    // 并行执行精确搜索和模糊搜索
                    const [exactResult, fuzzyResult] = await Promise.all([
                        store.findNode(apiNodeName.value),
                        store.fuzzyFindNode(apiNodeName.value)
                    ])
                    console.log('exactResult', exactResult)
                    console.log('fuzzyResult', fuzzyResult)

                    // 处理精确匹配结果
                    if (exactResult && exactResult.data) {
                        exactMatches.value = [exactResult.data]
                    } else {
                        exactMatches.value = []
                    }

                    // 处理模糊匹配结果
                    if (fuzzyResult && fuzzyResult.data && Array.isArray(fuzzyResult.data)) {
                        // 过滤掉与精确匹配重复的结果
                        const exactIds = new Set(exactMatches.value.map(node => node.id))
                        fuzzyMatches.value = fuzzyResult.data.filter(node => !exactIds.has(node.id))
                    } else {
                        fuzzyMatches.value = []
                    }

                    // 如果没有匹配结果，隐藏建议列表
                    if (exactMatches.value.length === 0 && fuzzyMatches.value.length === 0) {
                        showSuggestions.value = false
                    }
                } catch (error) {
                    console.error('搜索节点失败:', error)
                    exactMatches.value = []
                    fuzzyMatches.value = []
                    showSuggestions.value = false
                }
            }, 300) // 300ms延迟
        }

        // 选择建议项
        const selectSuggestion = (nodeName) => {
            apiNodeName.value = nodeName
            showSuggestions.value = false

            // 将node作为节点保存到数据中
            // 查找选中的节点数据
            let selectedNode = null
            // 先在精确匹配中查找
            selectedNode = exactMatches.value.find(node => node.name === nodeName)
            // 如果没找到，再在模糊匹配中查找
            if (!selectedNode) {
                selectedNode = fuzzyMatches.value.find(node => node.name === nodeName)
            }

            // 如果找到了节点数据，则添加到store中
            if (selectedNode) {
                const tempNode = {
                    id: selectedNode.id,
                    name: selectedNode.name,
                    desc: selectedNode.desc || '',
                    symbolSize: selectedNode.symbolSize || 1,
                    category: selectedNode.category || ['default'],
                    times: 0,
                    style: {
                        fill: '#5B8FF9',
                        stroke: '#fff',
                        lineWidth: 2
                    },
                    locked: false
                }
                store.addNode(tempNode)
            }

            // 触发筛选事件更新高亮
            emit('filter-change', {
                searchText: apiNodeName.value
            })
        }

        // 处理键盘事件
        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                showSuggestions.value = false
            }
        }

        // 处理节点描述输入
        const handleNodeDescInput = () => {
            // 触发筛选事件，即使输入为空也要触发以清除高亮
            emit('filter-change', {
                searchText: apiNodeDesc.value
            })
        }

        // 添加为子节点功能
        const addNodeToChild = async () => {
            if (!apiNodeName.value.trim()) {
                alert('请输入节点名称')
                return
            }

            if (!props.selectedNode) {
                alert('请先选择一个节点')
                return
            }

            if (!selectedGraphTypeId.value) {
                alert('请选择关系类型')
                return
            }

            try {
                // 调用store的addNodeToChild方法
                // 新节点作为fromNode，选中节点作为toNode
                const response = await store.addNodeToChild(
                    props.selectedNode.id,
                    apiNodeName.value,
                    selectedGraphTypeId.value,
                    apiNodeDesc.value
                )

                console.log('添加子节点成功:', response)
                alert('子节点添加成功')

                // 重置表单
                apiNodeName.value = ''
                apiNodeDesc.value = ''
                apiNodeImage.value = ''
            } catch (error) {
                console.error('添加子节点失败:', error)
                alert('子节点添加失败: ' + error.message)
            }
        }
        const addChildNode = async () => {
            console.log('添加为子节点')
            console.log('selectedNode:', props.selectedNode.id)
            console.log("nodeName:", apiNodeName.value)
            console.log("nodeDesc:", apiNodeDesc.value)
            console.log("graphId:", selectedGraphTypeId.value)
            if (!apiNodeName.value.trim()) {
                alert('请输入节点名称')

                return
            }
            if (!selectedGraphTypeId.value) {
                alert('请选择关系类型')

                return
            }
            try {
                const response = await store.addChildNode(props.selectedNode.id, apiNodeName.value, selectedGraphTypeId.value, apiNodeDesc.value)
                console.log('添加子节点成功:', response)
                alert('子节点添加成功')
                // 重置表单
                apiNodeName.value = ''
                apiNodeDesc.value = ''
                apiNodeImage.value = ''
            } catch (error) {
                console.error('添加子节点失败:', error)
                alert('子节点添加失败: ' + error.message)
            }
        }

        // }
        // API添加节点
        const addNodeAPI = async () => {
            if (!apiNodeName.value.trim()) {
                alert('请输入节点名称')
                return
            }

            try {
                const response = await store.addNodeAPI(apiNodeName.value, apiNodeDesc.value)
                console.log('API添加节点成功:', response)
                alert('节点添加成功')

                // 重置表单
                apiNodeName.value = ''
                apiNodeDesc.value = ''
                apiNodeImage.value = ''



            } catch (error) {
                console.error('API添加节点失败:', error)
                alert('节点添加失败: ' + error.message)
            }
        }

        // const addNodeToView = () => {
        // tempNode["id"] = tempId;
        // tempNode["name"] = item["newName"];
        // tempNode["desc"] = item["newDesc"];
        // tempNode["symbolSize"] = item["value"];
        // tempNode["category"] = ["default"];//MARK这里暂时还没有明确怎么填
        // //设置被点击次数
        // tempNode["times"] = 0;
        // // 添加样式属性
        // tempNode["style"] = {
        //     fill: '#5B8FF9',
        //     stroke: '#fff',
        //     lineWidth: 2
        // };
        // tempNode["locked"] = false;

        // }
        // 添加节点关系
        const addRelationship = async () => {
            if (!sourceNodeId.value.trim() || !targetNodeId.value.trim()) {
                alert('请先选择源节点和目标节点')
                return
            }

            if (sourceNodeId.value === targetNodeId.value) {
                alert('源节点和目标节点不能相同')
                return
            }

            try {
                // 这里需要根据实际API调整参数
                const response = await store.addGraph(sourceNodeId.value, targetNodeId.value, selectedGraphTypeId.value || 1)
                console.log('添加节点关系成功:', response)
                // alert('节点关系添加成功')
                // 更新图数据




            } catch (error) {
                console.error('添加节点关系失败:', error)
                alert('添加节点关系失败: ' + error.message)
            }
        }

        // 添加分组
        const addGroupAPI = async () => {
            if (!groupName.value.trim()) {
                alert('请输入分组名称')
                return
            }

            try {
                // 创建分组对象
                const newGroup = {
                    name: groupName.value,
                    color: groupColor.value
                }

                // 调用store中的添加分组方法
                store.addGroup(newGroup)
                console.log('分组添加成功')
                alert('分组添加成功')

                // 重置表单
                groupName.value = ''
                groupColor.value = '#5B8FF9'
            } catch (error) {
                console.error('添加分组失败:', error)
                alert('添加分组失败: ' + error.message)
            }
        }

        // 查找节点
        const findNode = async () => {
            if (!searchNodeName.value.trim()) {
                alert('请输入要搜索的节点名称')
                return
            }

            try {
                const response = await store.findNode(searchNodeName.value)
                console.log('查找节点结果:', response)
                alert('查找完成，请查看控制台结果')
            } catch (error) {
                console.error('查找节点失败:', error)
                alert('查找节点失败: ' + error.message)
            }
        }

        // 模糊查找节点
        const fuzzyFindNode = async () => {
            if (!searchNodeName.value.trim()) {
                alert('请输入要搜索的节点名称')
                return
            }

            try {
                const response = await store.fuzzyFindNode(searchNodeName.value)
                console.log('模糊查找节点结果:', response)
                alert('模糊查找完成，请查看控制台结果')
            } catch (error) {
                console.error('模糊查找节点失败:', error)
                alert('模糊查找节点失败: ' + error.message)
            }
        }

        // 处理筛选变化
        const handleFilterChange = () => {
            emit('filter-change', {
                groups: filterGroups.value,
                searchText: searchText.value
            })
        }

        // 处理搜索
        const handleSearch = () => {
            emit('search', searchText.value)
        }

        // 交换源节点和目标节点
        const swapNodes = () => {
            // 交换ID
            const tempId = sourceNodeId.value
            sourceNodeId.value = targetNodeId.value
            targetNodeId.value = tempId

            // 交换显示文本
            const tempDisplay = sourceNodeDisplay.value
            sourceNodeDisplay.value = targetNodeDisplay.value
            targetNodeDisplay.value = tempDisplay
        }

        // 监听筛选分组变化
        watch(filterGroups, () => {
            handleFilterChange()
        })

        return {
            apiNodeName,
            apiNodeDesc,
            apiNodeImage,
            searchNodeName,
            sourceNodeId,
            targetNodeId,
            relationshipDesc,
            groupName,
            groupColor,
            filterGroups,
            searchText,
            nodes,
            groups,
            graphTypes,
            selectedGraphTypeId,
            sourceNodeDisplay,
            targetNodeDisplay,
            showSuggestions,
            exactMatches,
            fuzzyMatches,
            addNodeAPI,
            addRelationship,
            addGroupAPI,
            findNode,
            fuzzyFindNode,
            handleSearch,
            addChildNode,
            addNodeToChild,  // 添加新方法
            swapNodes,
            handleNodeNameInput,
            handleNodeDescInput,
            selectSuggestion,
            handleKeydown
        }
    }
}
</script>

<style scoped>
.graph-toolbar {
    padding: 20px;
    height: 90%;
    overflow-y: auto;
}

.toolbar-section {
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
}

.toolbar-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
}

.toolbar-section h3 {
    margin-top: 0;
    margin-bottom: 15px;
    color: #333;
    font-size: 16px;
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: #555;
}

.form-control {
    width: 90%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-control[multiple] {
    height: 80px;
}

.color-picker {
    height: 40px;
    padding: 2px;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    display: inline-block;
    text-align: center;
}

.btn-block {
    display: block;
    width: 100%;
}

.btn-primary {
    background: #007bff;
    color: white;
}

.btn-primary:hover {
    background: #0056b3;
}

.btn-secondary {
    background: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background: #545b62;
}

/* 节点输入容器 */
.node-input-container {
    display: flex;
    gap: 5px;
}

/* 交换按钮 */
.swap-btn {
    width: 36px;
    height: 36px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.swap-btn:hover {
    background: #0056b3;
}

/* 自动补全样式 */
.autocomplete-wrapper {
    position: relative;
}

.suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 200px;
    overflow-y: auto;
    margin-top: 2px;
}

.suggestion-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
}

.suggestion-item:hover {
    background-color: #f5f5f5;
}

.suggestion-item:last-child {
    border-bottom: none;
}

.exact-match {
    color: #1890ff;
    /* 蓝色 */
    font-weight: 500;
}

.fuzzy-match {
    color: #faad14;
    /* 黄色 */
}
</style>